import { GetStaticPaths, GetStaticPathsContext } from 'next';
import { Dict } from '@ts/general';
import { getMonthNames, I18N_KEY_TYPE, LOCALE_CODES } from '@constants';
import { en, es } from '../public/locales';
import { capitalize } from './strings';

export const withLocalizedPaths = (fn: GetStaticPaths) => {
	return async (context: GetStaticPathsContext) => {
		try {
			const { locales } = context;
			const { paths, fallback } = await fn(context);
			const localizedPaths = [];

			paths.forEach(path => {
				if (typeof path === 'string') {
					return;
				}
				return locales.forEach(locale => {
					localizedPaths.push({
						params: path.params,
						locale,
					});
				});
			});

			return {
				paths: localizedPaths,
				fallback,
			};
		} catch (error) {
			console.error(error);
		}
	};
};

type Translate = (key: string, locale: string, args?: Dict) => string;

export const translate: Translate = (key, locale, args = {}) => {
	const translations = {
		[LOCALE_CODES.MX]: es,
		[LOCALE_CODES.US]: en,
	};

	const translationDict = translations[locale] || en;
	const translation = translationDict[key] || key;

	return typeof translation === 'function' ? translation(args) : translation;
};

export const transformTextToKey = (text: string, textType?: I18N_KEY_TYPE): string => {
	if (!text) return text;

	const normalizedText = text.replace(/™/g, '').replace(/\s+/g, '-').toLowerCase();

	return textType ? `${textType}-${normalizedText}` : normalizedText;
};

export const getLocalizedText = (currentText: string, i18nType: I18N_KEY_TYPE, locale: string) => {
	const i18nKeyFrameMaterials = transformTextToKey(currentText, i18nType);
	const translatedText = translate(i18nKeyFrameMaterials, locale);

	const localeValue = translatedText.startsWith(`${i18nType}-`) ? currentText : translatedText;

	return localeValue;
};

export function localizeMonthInDate(dateFormated: string, locale: string): string {
	const localizedMonths = getMonthNames(locale);
	const dateFormatedAllUpper = dateFormated.toUpperCase();
	const monthsNames = Object.keys(localizedMonths);

	for (const month of monthsNames) {
		const regex = new RegExp(`\\b${month.toUpperCase()}\\b`, 'i');
		if (regex.test(dateFormatedAllUpper)) {
			const matchedMonth = dateFormatedAllUpper.match(regex)?.[0] || '';
			const localizedMonth = localizedMonths[matchedMonth] || capitalize(month);
			return dateFormatedAllUpper.replace(matchedMonth, localizedMonth);
		}
	}
	return dateFormated;
}

export function localizeVariantTitle(variantTitle: string, locale: string): string {
	if (!variantTitle) return variantTitle;
	const parts = variantTitle
		.split('/')
		.map(part => part.trim())
		.filter(part => part && part.toLowerCase() !== 'none');

	const [color, rxType] = parts;

	const localizedParts: string[] = [];

	if (color) {
		localizedParts.push(getLocalizedText(color, I18N_KEY_TYPE.COLOR, locale));
	}

	if (rxType) {
		localizedParts.push(getLocalizedText(rxType, I18N_KEY_TYPE.RX_TYPE, locale));
	}

	return localizedParts.join(' / ');
}
